import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { copyToClipboard } from "../lib/clipboard";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";
import { Plus, Trash, ShieldCheck, UserMinus, UserPlus, Copy, ArrowsLeftRight, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_STYLES = {
  owner: "bg-zinc-900 text-white",
  cfo: "bg-[#c9a24a] text-zinc-950",
  partner: "bg-emerald-600 text-white",
  distributor: "bg-zinc-100 text-zinc-700 border border-zinc-200",
};

export default function UsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCfo, setShowCfo] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const promoteToCfo = async (u) => {
    if (!window.confirm(t("users.promote_to_cfo", { name: u.name }))) return;
    try {
      await api.patch(`/users/${u.id}`, { role: "cfo" });
      toast.success(t("users.now_cfo", { name: u.name }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const demoteToDistributor = async (u) => {
    if (!window.confirm(t("users.demote_to_distributor", { name: u.name }))) return;
    try {
      await api.patch(`/users/${u.id}`, { role: "distributor" });
      toast.success(t("users.now_distributor", { name: u.name }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? t("users.deactivated") : t("users.activated"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(t("users.remove_confirm", { name: u.name }))) return;
    try {
      const { data } = await api.delete(`/distributors/${u.id}`);
      toast.success(t("users.removed_n", { n: data.deleted_users || 1 }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetUserPassword = async (u) => {
    if (!window.confirm(t("users.reset_confirm", { name: u.name }))) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setCreatedCreds({ email: data.email, name: data.name, password: data.temp_password, isReset: true });
      toast.success(t("users.reset_done"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("users.title")}</h1>
          <p className="text-sm sm:text-base text-zinc-600 mt-1">
            {t("users.subtitle")}
          </p>
        </div>
        <div className="flex gap-2">
          <BrutalButton
            variant="secondary"
            onClick={() => navigate("/user-audit")}
            data-testid="open-user-audit-btn"
          >
            <ShieldCheck size={16} weight="bold" /> {t("user_audit.title")}
          </BrutalButton>
          <BrutalButton onClick={() => setShowCfo(true)} data-testid="add-cfo-btn">
            <UserPlus size={16} weight="bold" /> {t("users.add_cfo")}
          </BrutalButton>
        </div>
      </div>

      <div className="border border-zinc-200 bg-zinc-50/50 rounded-xl p-4 text-sm text-zinc-700">
        <p>{t("users.info")}</p>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500">{t("common.loading")}</p>
      ) : (
        <BrutalCard className="!p-0 overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-3 border-b border-zinc-200 bg-zinc-50 text-xs font-medium text-zinc-500">
            <div className="col-span-4">{t("users.col_name_email")}</div>
            <div className="col-span-2">{t("users.col_role")}</div>
            <div className="col-span-3">{t("users.col_reports_to")}</div>
            <div className="col-span-1">{t("users.col_depth")}</div>
            <div className="col-span-2 text-right">{t("users.col_actions")}</div>
          </div>
          <div className="divide-y divide-zinc-100">
            {users.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                onPromote={() => promoteToCfo(u)}
                onDemote={() => demoteToDistributor(u)}
                onToggle={() => toggleActive(u)}
                onRemove={() => removeUser(u)}
                onResetPassword={() => resetUserPassword(u)}
              />
            ))}
          </div>
        </BrutalCard>
      )}

      {showCfo && (
        <CreateCfoDialog
          onClose={() => setShowCfo(false)}
          onCreated={(creds) => { setCreatedCreds(creds); load(); }}
        />
      )}

      {createdCreds && (
        <CredsDialog creds={createdCreds} onClose={() => setCreatedCreds(null)} />
      )}
    </div>
  );
}

function UserRow({ user, onPromote, onDemote, onToggle, onRemove, onResetPassword }) {
  const { t } = useTranslation();
  const ROLE_LABEL = {
    owner: t("roles.owner"),
    cfo: t("roles.cfo"),
    partner: t("roles.partner"),
    distributor: t("roles.distributor"),
    admin: t("roles.owner"),
  };
  const isOwner = user.role === "owner" || user.role === "admin";
  return (
    <div
      data-testid={`user-row-${user.id}`}
      className={`grid grid-cols-1 sm:grid-cols-12 gap-3 px-5 py-4 items-center ${user.active === false ? "opacity-50" : ""}`}
    >
      <div className="sm:col-span-4 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-medium text-zinc-950 truncate">{user.name}</div>
          {user.active === false && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-full">{t("users.inactive")}</span>
          )}
          {user.must_change_password && (
            <button
              type="button"
              onClick={onResetPassword}
              className="text-[10px] font-medium px-1.5 py-0.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-300 rounded-full inline-flex items-center gap-1 transition-colors cursor-pointer"
              data-testid={`pending-setup-${user.id}`}
              title={t("users.pending_setup_resend_tip")}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {t("users.pending_setup")}
            </button>
          )}
        </div>
        <div className="text-xs text-zinc-500 truncate">{user.email}</div>
      </div>
      <div className="sm:col-span-2">
        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${ROLE_STYLES[user.role] || ROLE_STYLES.distributor}`}>
          {ROLE_LABEL[user.role] || user.role}
        </span>
      </div>
      <div className="sm:col-span-3 text-sm text-zinc-600 truncate">{user.parent_name || "—"}</div>
      <div className="sm:col-span-1 text-sm text-zinc-500">{user.depth ?? 0}</div>
      <div className="sm:col-span-2 flex justify-end gap-2">
        {!isOwner && user.role === "distributor" && (
          <BrutalButton size="sm" variant="secondary" onClick={onPromote} data-testid={`promote-${user.id}`}>
            <ShieldCheck size={14} weight="bold" /> {t("roles.cfo")}
          </BrutalButton>
        )}
        {!isOwner && user.role === "cfo" && (
          <BrutalButton size="sm" variant="secondary" onClick={onDemote} data-testid={`demote-${user.id}`}>
            <ArrowsLeftRight size={14} weight="bold" /> {t("roles.distributor")}
          </BrutalButton>
        )}
        {!isOwner && (
          <BrutalButton size="sm" variant="secondary" onClick={onResetPassword} data-testid={`reset-pw-${user.id}`} title={t("users.reset_password")}>
            <Key size={14} weight="bold" />
          </BrutalButton>
        )}
        {!isOwner && (
          <BrutalButton size="sm" variant="secondary" onClick={onToggle} data-testid={`toggle-${user.id}`}>
            <UserMinus size={14} weight="bold" />
          </BrutalButton>
        )}
        {!isOwner && (
          <BrutalButton size="sm" variant="warning" onClick={onRemove} data-testid={`remove-${user.id}`}>
            <Trash size={14} weight="bold" />
          </BrutalButton>
        )}
      </div>
    </div>
  );
}

function CreateCfoDialog({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/users/cfo", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      toast.success(t("users.cfo_created_title"));
      onCreated({ ...data, password: form.password });
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} testId="create-cfo-dialog">
      <h2 className="text-lg font-semibold mb-1">{t("users.cfo_dialog_title")}</h2>
      <p className="text-sm text-zinc-600 mb-4">{t("users.cfo_dialog_subtitle")}</p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1">{t("auth.full_name")}</label>
          <BrutalInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="cfo-name-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1">{t("common.email")}</label>
          <BrutalInput type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="cfo-email-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1">{t("common.password")}</label>
          <BrutalInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="cfo-password-input" />
        </div>
        <div className="flex gap-2 pt-1">
          <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>{t("common.cancel")}</BrutalButton>
          <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="cfo-submit">{busy ? t("users.creating") : t("users.create_cfo")}</BrutalButton>
        </div>
      </form>
    </Modal>
  );
}

function CredsDialog({ creds, onClose }) {
  const { t } = useTranslation();
  const isReset = !!creds.isReset;
  return (
    <Modal onClose={onClose} testId="creds-dialog">
      <h2 className="text-lg font-semibold mb-1">
        {isReset ? t("users.reset_done_title") : t("users.cfo_created_title")}
      </h2>
      <p className="text-sm text-zinc-600 mb-3">
        {isReset ? t("users.reset_done_subtitle", { name: creds.name || creds.email }) : t("users.cfo_created_subtitle")}
      </p>
      <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 font-mono text-sm space-y-1">
        <div><strong>{t("common.email")}:</strong> {creds.email}</div>
        <div><strong>{t("common.password")}:</strong> {creds.password}</div>
      </div>
      <BrutalButton
        className="w-full mt-3"
        onClick={() => {
          copyToClipboard(
            `Email: ${creds.email}\nPassword: ${creds.password}\nLogin: ${window.location.origin}/login`,
            t("common.copied")
          );
        }}
        data-testid="cfo-created-copy"
      >
        <Copy size={14} weight="bold" /> {t("distributors.copy_creds")}
      </BrutalButton>
      <BrutalButton variant="secondary" className="w-full mt-2" onClick={onClose} data-testid="cfo-created-done">{t("common.done")}</BrutalButton>
    </Modal>
  );
}

function Modal({ children, onClose, testId }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-5 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {children}
      </div>
    </div>
  );
}
